import {
    AnnualGiftingStrategy,
    EstateTaxBreakdown,
    GiftToGrantorTrustStrategy,
    GRATStrategy,
    initialStateOfResidencyStrategy,
    StateInputDto,
    StateOfResidencyStrategy,
    StrategyDetailsSectionInfo,
    StrategyType
} from "./WealthPOTypes";
import moment from "moment/moment";
import {calculateYearsBetweenWithoutDecimals, getTodayDateAsString} from "../utils/dateUtils";
import {DISPLAY_DATE_FORMAT, ISO8601_DATE_FORMAT} from "../constants/common";
import {
    InvestorGroupMemberType,
    InvestorGroupType,
    PlanningPeriodType
} from "../ClientManagement/models/InvestorGroupType";
import {GRATErrorConditions} from "./AddEditGRAT";
import {GTGTExemption} from "../WealthTransfer/components/EstateTaxRunningTotal";
import {
    EstateTaxBreakdownReportRowProps
} from "../Reporting/DetailedEmailReports/WealthPlanOptimizerReport/EstateTaxBreakdownReportRow";
import {EstateTaxBreakdownReportContent} from "./components/EstateTaxBreakdownContent";
import {v4 as randomUuid} from "uuid";

export interface PlanningPeriodValues {
    birthdate: string | null,
    firstName: string,
    planningPeriod: PlanningPeriodType | undefined
}

export interface DateAndAgeValues {
    startDate: string,
    endDate: string
    startYear: number | string,
    endYear: number | string,
    startAge: number | string,
    endAge: number | string
}

export interface InvestorGroupPostProcessingResponse {
    planningPeriodComputedValues: PlanningPeriodValues,
    dateAndAgeComputedValues: DateAndAgeValues
}

export interface StrategyTimeFrameValidationResponse {
    errorPresent: boolean,
    newYearsUntilFlow: number,
    newYearsOfFlow: number,
    errorType?: GRATErrorConditions
}

export const getStateNameForStateOfResidencyId = (listOfStates: StateInputDto[], id: string) => {

    const stateInputDto = listOfStates.find(state => state.id === `US-${id}`);

    if (stateInputDto) {
        const estateTaxPercentage = stateInputDto.forecastedEstateTaxRate * 100;
        return `${stateInputDto.name} (${estateTaxPercentage}%)`
    }

    return "";
};

export const getStateNamesForStateOfResidencyDropdown = (listOfStates: StateInputDto[]) => {
    return listOfStates.map((stateInputDto) => {
        const estateTaxPercentage = stateInputDto.forecastedEstateTaxRate * 100
        return `${stateInputDto.name} (${estateTaxPercentage}%)`
    });
};

export const getStateAbbreviationFromSelectedStateValue = (listOfStates: StateInputDto[], selectedState: string | undefined) => {
    if (selectedState === undefined) {
        return "";
    }

    const stateInputDTO = listOfStates.find(stateInputDtoFromList => {
        const estateTaxPercentage = stateInputDtoFromList.forecastedEstateTaxRate * 100
        return `${stateInputDtoFromList.name} (${estateTaxPercentage}%)` === selectedState;
    });

    return stateInputDTO?.id.split("-")[1];
};

export const getStateNameAndRateFromStateAbbreviation = (listOfStates: StateInputDto[], stateAbbreviation: string | undefined) => {
    if (stateAbbreviation === undefined) {
        return "";
    }

    const selectedStateId: string = "US-" + stateAbbreviation
    let stateNameAndRate;

    const selectStateInputDto = listOfStates.find(stateInformationFromList => stateInformationFromList.id === selectedStateId);

    if (selectStateInputDto) {
        const estateTaxPercentage = selectStateInputDto.forecastedEstateTaxRate * 100;
        stateNameAndRate = `${selectStateInputDto.name} (${estateTaxPercentage}%)`;
    }
    return stateNameAndRate;
}

export const validateYearsUntilFlow = (value: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues) => {
    let errorPresent = (value + yearsOfFlow > planningPeriodValues.planningPeriod!.numberOfYears);
    let newYearsUntilFlow: number = yearsUntilFlow;

    if (!errorPresent) {
        newYearsUntilFlow = value
    }

    return {
        errorPresent,
        newYearsUntilFlow,
        newYearsOfFlow: yearsOfFlow
    } as StrategyTimeFrameValidationResponse
}

export const validateYearsOfFlow = (value: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues) => {
    let errorPresent = (yearsUntilFlow + value > planningPeriodValues.planningPeriod!.numberOfYears);
    let newYearsOfFlow: number = yearsOfFlow

    if (!errorPresent) {
        newYearsOfFlow = value
    }

    return {
        errorPresent,
        newYearsUntilFlow: yearsUntilFlow,
        newYearsOfFlow
    } as StrategyTimeFrameValidationResponse;
}

export const validateYearsOfFlowRollingGrat = (value: number, yearsOfFlow: number, yearsLeftInPlanningPeriod: number | undefined) => {
    const ROLLING_GRAT_YOF_LOWER_BOUND = 3;
    const ROLLING_GRAT_YOF_UPPER_BOUND = 10;
    const ROLLING_GRAT_YUF = 0;

    let errorOutOfBounds: boolean = Number.isNaN(value) ||
        value < ROLLING_GRAT_YOF_LOWER_BOUND ||
        value > ROLLING_GRAT_YOF_UPPER_BOUND;

    if (errorOutOfBounds) {
        return {
            errorPresent: true,
            newYearsUntilFlow: ROLLING_GRAT_YUF,
            newYearsOfFlow: yearsOfFlow,
            errorType: GRATErrorConditions.YOF_OUT_OF_BOUNDS
        } as StrategyTimeFrameValidationResponse;
    }

    let errorExceedsPlanningPeriod: boolean = yearsLeftInPlanningPeriod !== undefined && value > yearsLeftInPlanningPeriod;

    if (errorExceedsPlanningPeriod) {
        return {
            errorPresent: true,
            newYearsUntilFlow: ROLLING_GRAT_YUF,
            newYearsOfFlow: yearsLeftInPlanningPeriod,
            errorType: GRATErrorConditions.YOF_PLANNING_PERIOD_YEARS
        } as StrategyTimeFrameValidationResponse;
    }

    return {
        errorPresent: false,
        newYearsUntilFlow: ROLLING_GRAT_YUF,
        newYearsOfFlow: value
    } as StrategyTimeFrameValidationResponse;
}

export const validateStartYear = (newStartYear: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues, dateAndAgeValues: DateAndAgeValues) => {
    let errorPresent = false;
    const latestYearToStartGoal = (planningPeriodValues.planningPeriod!.startYear + planningPeriodValues.planningPeriod!.numberOfYears) - yearsOfFlow;
    let newYearsUntilFlow: number = yearsUntilFlow;

    if (!dateAndAgeValues.startYear) {
        errorPresent = true;
    } else if (newStartYear < moment().year()) {
        errorPresent = true;
        newYearsUntilFlow = 0;
    } else if (newStartYear > latestYearToStartGoal) {
        errorPresent = true;
    }

    if (!errorPresent) {
        let difference: number = newStartYear - Number(dateAndAgeValues.startYear);
        newYearsUntilFlow = yearsUntilFlow + difference;
    }

    return {
        errorPresent,
        newYearsUntilFlow,
        newYearsOfFlow: yearsOfFlow
    } as StrategyTimeFrameValidationResponse
}

export const validateEndYear = (newEndYear: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues, dateAndAgeValues: DateAndAgeValues) => {
    let errorPresent = false;
    let newYearsOfFlow: number = yearsOfFlow;

    if (!dateAndAgeValues.endYear) {
        errorPresent = true;
    } else if (newEndYear > planningPeriodValues.planningPeriod!.startYear + planningPeriodValues.planningPeriod!.numberOfYears) {
        errorPresent = true;
        newYearsOfFlow = planningPeriodValues.planningPeriod!.numberOfYears - yearsUntilFlow;
    } else if (newEndYear <= Number(dateAndAgeValues.startYear)) {
        errorPresent = true;
        newYearsOfFlow = 1;
    }

    if (!errorPresent) {
        let difference: number = newEndYear - Number(dateAndAgeValues.endYear);
        newYearsOfFlow = yearsOfFlow + difference;
    }

    return {
        errorPresent,
        newYearsUntilFlow: yearsUntilFlow,
        newYearsOfFlow
    }
}

export const validateStartAge = (newStartAge: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues, dateAndAgeValues: DateAndAgeValues) => {
    let errorPresent = false;
    let newYearsUntilFlow: number = yearsUntilFlow;
    let clientAge = moment().diff(planningPeriodValues.birthdate!, "years")

    if (!dateAndAgeValues.startAge) {
        errorPresent = true;
    } else if (newStartAge < clientAge) {
        errorPresent = true;
        newYearsUntilFlow = 0;
    } else if (newStartAge >= Number(dateAndAgeValues.endAge)) {
        errorPresent = true;
    }

    if (!errorPresent) {
        let difference: number = newStartAge - Number(dateAndAgeValues.startAge)
        newYearsUntilFlow = yearsUntilFlow + difference;
    }

    return {
        errorPresent,
        newYearsUntilFlow,
        newYearsOfFlow: yearsOfFlow
    } as StrategyTimeFrameValidationResponse
}

export const validateEndAge = (newEndAge: number, yearsUntilFlow: number, yearsOfFlow: number, planningPeriodValues: PlanningPeriodValues, dateAndAgeValues: DateAndAgeValues) => {
    let errorPresent = false;
    let newYearsOfFlow: number = yearsOfFlow;
    let availableYearsOfFlow = planningPeriodValues.planningPeriod!.numberOfYears - yearsUntilFlow;
    let maxAge = Number(dateAndAgeValues.startAge) + availableYearsOfFlow;

    if (!dateAndAgeValues.endAge) {
        errorPresent = true;
    } else if (newEndAge < Number(dateAndAgeValues.startAge)) {
        errorPresent = true;
        newYearsOfFlow = 1
    } else if (newEndAge > maxAge) {
        errorPresent = true;
        newYearsOfFlow = availableYearsOfFlow;
    }

    if (!errorPresent) {
        let difference: number = newEndAge - Number(dateAndAgeValues.endAge)
        newYearsOfFlow = yearsOfFlow + difference;
    }

    return {
        errorPresent,
        newYearsUntilFlow: yearsUntilFlow,
        newYearsOfFlow
    }
}

export const validateDescription = (description: string): boolean => {
    return !(!description.trim());
}

export const processInvestorGroupResponse = (investorGroup: InvestorGroupType, yearsUntilFlow: number, yearsOfFlow: number): InvestorGroupPostProcessingResponse => {
    let dateAndAgeComputedValues = {
        startDate: "",
        endDate: "",
        startYear: "",
        endYear: "",
        startAge: "",
        endAge: ""
    } as DateAndAgeValues

    let planningPeriodComputedValues = {
        birthdate: "",
        firstName: "",
        planningPeriod: undefined
    } as PlanningPeriodValues

    if (investorGroup.planningPeriod.memberType === InvestorGroupMemberType.PRIMARY) {
        planningPeriodComputedValues = {
            birthdate: investorGroup.primaryMember.birthdate,
            firstName: investorGroup.primaryMember.firstName,
            planningPeriod: investorGroup.planningPeriod
        }

        dateAndAgeComputedValues = getDateAndAgeValues(investorGroup.primaryMember.birthdate, yearsUntilFlow, yearsOfFlow)
    } else if (investorGroup.planningPeriod.memberType === InvestorGroupMemberType.PARTNER && investorGroup.partnerMember) {
        planningPeriodComputedValues = {
            birthdate: investorGroup.partnerMember.birthdate,
            firstName: investorGroup.partnerMember.firstName,
            planningPeriod: investorGroup.planningPeriod
        }

        dateAndAgeComputedValues = getDateAndAgeValues(investorGroup.partnerMember.birthdate, yearsUntilFlow, yearsOfFlow)
    }

    return {
        planningPeriodComputedValues,
        dateAndAgeComputedValues
    } as InvestorGroupPostProcessingResponse
}

export const getDateAndAgeValues = (birthday: string | null, yearsUntilFlow: number, yearsOfFlow: number): DateAndAgeValues => {
    if (birthday) {
        const todayDate: string = getTodayDateAsString();

        const yearsBetween = Math.floor(calculateYearsBetweenWithoutDecimals(birthday, todayDate));

        const startDateBeforeFormatting = moment(birthday).add(yearsBetween + yearsUntilFlow, 'years')
        const endDateBeforeFormatting = moment(startDateBeforeFormatting).add(yearsOfFlow, 'years');

        const startDate = startDateBeforeFormatting.format(DISPLAY_DATE_FORMAT)
        const endDate = endDateBeforeFormatting.format(DISPLAY_DATE_FORMAT)

        return {
            startDate,
            endDate,
            startYear: startDateBeforeFormatting.year().toString(),
            endYear: endDateBeforeFormatting.year().toString(),
            startAge: moment(startDateBeforeFormatting, ISO8601_DATE_FORMAT).diff(birthday, 'years', false).toString(),
            endAge: moment(endDateBeforeFormatting, ISO8601_DATE_FORMAT).diff(birthday, 'years', false).toString()
        } as DateAndAgeValues;
    }

    return {
        startDate: "",
        endDate: "",
        startYear: "",
        endYear: "",
        startAge: "",
        endAge: ""
    } as DateAndAgeValues;
}

export const convertStrategyTypeToString = (strategyType: StrategyType) => {
    let type: string = "";

    switch (strategyType) {
        case StrategyType.ANNUAL_GIFTING:
            type = "Annual Gifting";
            break;
        case StrategyType.STATE_OF_RESIDENCY:
            type = "State of Residency";
            break;
        case StrategyType.GIFT_TO_GRANTOR_TRUST:
            type = "Gift to Grantor Trust";
            break;
        case StrategyType.TWO_YEAR_GRAT:
            type = "Two Year GRAT";
            break;
        case StrategyType.FIVE_YEAR_GRAT:
            type = "Five Year GRAT";
            break;
        case StrategyType.ROLLING_GRAT:
            type = "Rolling GRAT";
            break;
        default:
            return "";
    }

    return type;
}

const buildEstateTaxBreakdownRows = (estateTaxBreakdownWithoutStrategies: EstateTaxBreakdown,
                                     estateTaxBreakdownWithStrategies: EstateTaxBreakdown,
                                     taxType: string,
                                     enableEstateTaxGraduatedRates: boolean = false,
                                     strategyDetailsList?: StrategyDetailsSectionInfo[],
                                     sorDescription?: string,
                                     gtgtExemptions?: GTGTExemption[]): EstateTaxBreakdownReportRowProps[] => {
    const estateTaxBreakdownRows: EstateTaxBreakdownReportRowProps[] = [];
    const taxRateLabel = enableEstateTaxGraduatedRates && taxType === "State" ? "Effective State Estate" : taxType;

    const taxSummaryWithoutStrategies = taxType === "State"
        ? estateTaxBreakdownWithoutStrategies.stateTaxSummary
        : estateTaxBreakdownWithoutStrategies.federalTaxSummary;
    const taxSummaryWithStrategies = taxType === "State"
        ? estateTaxBreakdownWithStrategies.stateTaxSummary
        : estateTaxBreakdownWithStrategies.federalTaxSummary;

    if (taxSummaryWithoutStrategies.effectiveTaxRate > 0) {
        const estimatedEstateRowId = randomUuid();

        const estimatedEstate = {
            rowId: estimatedEstateRowId,
            title: "Estimated Estate",
            amount: estateTaxBreakdownWithoutStrategies.estateValue,
            amountWithStrategies: estateTaxBreakdownWithStrategies.estateValue,
            type: taxType
        } as EstateTaxBreakdownReportRowProps;
        estateTaxBreakdownRows.push(estimatedEstate);

        strategyDetailsList?.map(({amountToBeneficiaries, description}) =>
            estateTaxBreakdownRows.push({
                rowId: randomUuid(),
                parentRowId: estimatedEstateRowId,
                amountWithStrategies: -amountToBeneficiaries,
                description,
                type: taxType,
                isStrategyRow: true
            } as EstateTaxBreakdownReportRowProps)
        );

        if (estateTaxBreakdownWithStrategies.charitableDeduction > 0) {
            estateTaxBreakdownRows.push({
                rowId: randomUuid(),
                title: "Charitable Deduction",
                amount: -estateTaxBreakdownWithoutStrategies.charitableDeduction,
                amountWithStrategies: -estateTaxBreakdownWithoutStrategies.charitableDeduction,
                type: taxType
            } as EstateTaxBreakdownReportRowProps);
        }

        if (taxType === "State") {
            estateTaxBreakdownRows.push({
                rowId: randomUuid(),
                title: "State Exemption",
                amount: -taxSummaryWithoutStrategies.totalAvailableExemption,
                amountWithStrategies: -taxSummaryWithStrategies.totalAvailableExemption,
                type: "State"
            } as EstateTaxBreakdownReportRowProps);
        } else {
            estateTaxBreakdownRows.push({
                rowId: randomUuid(),
                title: "State Estate Tax Deduction",
                amount: -estateTaxBreakdownWithoutStrategies.stateTaxSummary.estimatedEstateTax,
                amountWithStrategies: -estateTaxBreakdownWithStrategies.stateTaxSummary.estimatedEstateTax,
                type: taxType
            } as EstateTaxBreakdownReportRowProps);

            const availableExemptionRowId = randomUuid();
            estateTaxBreakdownRows.push({
                rowId: availableExemptionRowId,
                title: "Available Exemption",
                amount: -taxSummaryWithoutStrategies.totalAvailableExemption,
                amountWithStrategies: -taxSummaryWithStrategies.totalAvailableExemption,
                type: taxType
            } as EstateTaxBreakdownReportRowProps);
            gtgtExemptions?.map(({exemptionAmount, description}) =>
                estateTaxBreakdownRows.push({
                    rowId: randomUuid(),
                    parentRowId: availableExemptionRowId,
                    amountWithStrategies: -exemptionAmount,
                    description,
                    type: taxType,
                    isStrategyRow: true
                } as EstateTaxBreakdownReportRowProps)
            );
        }

        estateTaxBreakdownRows.push({
            rowId: randomUuid(),
            title: "Taxable Estate",
            amount: taxSummaryWithoutStrategies.taxableEstateValue,
            amountWithStrategies: taxSummaryWithStrategies.taxableEstateValue,
            type: taxType,
            additionClassNames: ["subsection-header"]
        } as EstateTaxBreakdownReportRowProps);

        estateTaxBreakdownRows.push({
            rowId: randomUuid(),
            title: `* ${taxRateLabel} Tax Rate`,
            amount: taxSummaryWithoutStrategies.effectiveTaxRate,
            amountWithStrategies: taxSummaryWithStrategies.effectiveTaxRate,
            description: sorDescription,
            isTaxRateRow: true,
            type: taxType
        } as EstateTaxBreakdownReportRowProps);

        estateTaxBreakdownRows.push({
            rowId: randomUuid(),
            title: `Estimated ${taxType} Estate Tax`,
            amount: taxSummaryWithoutStrategies.estimatedEstateTax,
            amountWithStrategies: taxSummaryWithStrategies.estimatedEstateTax,
            type: taxType,
            additionClassNames: ["subsection-header"]
        } as EstateTaxBreakdownReportRowProps);
    }

    return estateTaxBreakdownRows;
}

const addParentRowIfStrategy = (
    rows: EstateTaxBreakdownReportRowProps[],
    currentRow: EstateTaxBreakdownReportRowProps
): EstateTaxBreakdownReportRowProps[] => {
    if (currentRow.isStrategyRow) {
        const parentRow = rows.find(row => row.rowId === currentRow.parentRowId);
        if (parentRow) {
            return [{ ...parentRow, title: parentRow.title + " (continued)" }];
        }
    }
    return [];
};

const getRowsForPage = (
    rows: EstateTaxBreakdownReportRowProps[],
    start: number,
    end: number
): EstateTaxBreakdownReportRowProps[] => rows.slice(start, end);

export const buildEstateTaxBreakdownReportContent = (estateTaxBreakdownRows: EstateTaxBreakdownReportRowProps[],
                                                     isContinued: boolean,
): EstateTaxBreakdownReportContent => {
    const stateTaxBreakdownRows = estateTaxBreakdownRows.filter(row => row.type === "State");
    const federalTaxBreakdownRows = estateTaxBreakdownRows.filter(row => row.type === "Federal");

    return {
        isContinued,
        showStateTaxBreakdown: stateTaxBreakdownRows.length > 0,
        stateTaxBreakdownRows,
        showFederalTaxBreakdown: federalTaxBreakdownRows.length > 0,
        federalTaxBreakdownRows
    } as EstateTaxBreakdownReportContent;
}

export const buildEstateTaxBreakdownRowsList = (estateTaxBreakdownWithoutStrategies: EstateTaxBreakdown,
                                                estateTaxBreakdownWithStrategies: EstateTaxBreakdown,
                                                enableEstateTaxGraduatedRates: boolean = false,
                                                strategyDetailsList?: StrategyDetailsSectionInfo[],
                                                sorDescription?: string,
                                                gtgtExemptions?: GTGTExemption[]): EstateTaxBreakdownReportRowProps[][] => {
    const estateTaxBreakdownRowsList: EstateTaxBreakdownReportRowProps[][] = [];

    const stateEstateTaxBreakdownRows = buildEstateTaxBreakdownRows(
        estateTaxBreakdownWithoutStrategies,
        estateTaxBreakdownWithStrategies,
        "State",
        enableEstateTaxGraduatedRates,
        strategyDetailsList,
        sorDescription);

    const federalEstateTaxBreakdownRows = buildEstateTaxBreakdownRows(
        estateTaxBreakdownWithoutStrategies,
        estateTaxBreakdownWithStrategies,
        "Federal",
        false,
        strategyDetailsList,
        undefined,
        gtgtExemptions);

    const estateTaxBreakdownRows = stateEstateTaxBreakdownRows.concat(federalEstateTaxBreakdownRows);

    for (let i = 0; i < estateTaxBreakdownRows.length;) {
        let endingIndex = i + 14;
        let estateTaxBreakdownRowsListItem: EstateTaxBreakdownReportRowProps[] = [];

        estateTaxBreakdownRowsListItem.push(
            ...addParentRowIfStrategy(estateTaxBreakdownRows, estateTaxBreakdownRows[i])
        );

        if (estateTaxBreakdownRowsListItem.length > 0) {
            endingIndex -= 1;
        }

        estateTaxBreakdownRowsListItem.push(
            ...getRowsForPage(estateTaxBreakdownRows, i, Math.min(endingIndex, estateTaxBreakdownRows.length))
        );

        estateTaxBreakdownRowsList.push(estateTaxBreakdownRowsListItem);
        i = endingIndex;
    }

    return estateTaxBreakdownRowsList;
}

export const buildStrategyDetailsList = (strategyCountPerPage: number,
                                         stateOfResidencyStrategy: StateOfResidencyStrategy | null,
                                         annualGiftingStrategyList: AnnualGiftingStrategy[],
                                         giftToGrantorTrustStrategyList: GiftToGrantorTrustStrategy[],
                                         gratStrategyList: GRATStrategy[]): {
    strategyDetailsList: StrategyDetailsSectionInfo[],
    listOfStrategyDetailsLists: StrategyDetailsSectionInfo[][]
} => {

    let strategyDetailsList: StrategyDetailsSectionInfo[] = []

    if (stateOfResidencyStrategy && (stateOfResidencyStrategy !== initialStateOfResidencyStrategy)) {
        strategyDetailsList.push({
            id: stateOfResidencyStrategy.id ?? "",
            type: StrategyType.STATE_OF_RESIDENCY,
            amountToBeneficiaries: stateOfResidencyStrategy.estimatedImpact.amountToBeneficiaries ?? 0,
            impactToEstEstateTax: stateOfResidencyStrategy.estimatedImpact.impactToEstEstateTax ?? 0,
            description: stateOfResidencyStrategy.description,
            enabled: stateOfResidencyStrategy.enabled
        });
    }

    annualGiftingStrategyList.forEach(annualGiftingStrategy => {
        strategyDetailsList.push({
            id: annualGiftingStrategy.strategyId ?? "",
            type: StrategyType.ANNUAL_GIFTING,
            description: annualGiftingStrategy.description,
            impactToEstEstateTax: annualGiftingStrategy.estimatedImpact.impactToEstEstateTax ?? 0,
            amountToBeneficiaries: annualGiftingStrategy.estimatedImpact.amountToBeneficiaries ?? 0,
            enabled: annualGiftingStrategy.enabled
        })
    });

    giftToGrantorTrustStrategyList.forEach(giftToGrantorTrustStrategy => {
        strategyDetailsList.push({
            id: giftToGrantorTrustStrategy.strategyId ?? "",
            type: StrategyType.GIFT_TO_GRANTOR_TRUST,
            description: giftToGrantorTrustStrategy.description,
            impactToEstEstateTax: giftToGrantorTrustStrategy.estimatedImpact.impactToEstEstateTax ?? 0,
            amountToBeneficiaries: giftToGrantorTrustStrategy.estimatedImpact.amountToBeneficiaries ?? 0,
            enabled: giftToGrantorTrustStrategy.enabled
        })
    });

    gratStrategyList.forEach(gratStrategy => {
        strategyDetailsList.push({
            id: gratStrategy.strategyId ?? "",
            type: gratStrategy.strategyType,
            description: gratStrategy.description,
            impactToEstEstateTax: gratStrategy.estimatedImpact.impactToEstEstateTax ?? 0,
            amountToBeneficiaries: gratStrategy.estimatedImpact.amountToBeneficiaries ?? 0,
            enabled: gratStrategy.enabled
        })
    });

    strategyDetailsList = sortStrategyDetails(strategyDetailsList);

    let listOfStrategyDetailsLists: StrategyDetailsSectionInfo[][] = []

    listOfStrategyDetailsLists.push(strategyDetailsList.slice(0, strategyCountPerPage))
    for (let i = strategyCountPerPage; i < strategyDetailsList.length; i += 10) {
        let endingIndex = i + 10

        if (endingIndex < strategyDetailsList.length) {
            listOfStrategyDetailsLists.push(strategyDetailsList.slice(i, endingIndex))
        } else {
            listOfStrategyDetailsLists.push(strategyDetailsList.slice(i))
            break;
        }
    }

    return {
        strategyDetailsList,
        listOfStrategyDetailsLists
    };
}

export const buildGTGTExemptionsList = (giftToGrantorTrustStrategyList: GiftToGrantorTrustStrategy[]): GTGTExemption[] => {
    return giftToGrantorTrustStrategyList
        ? giftToGrantorTrustStrategyList
            .filter(strategy => strategy.enabled)
            .map(enabledStrategy => ({
                exemptionAmount: enabledStrategy.estimatedImpact.giftAndExemptionSummary.federalExemptionUsed,
                description: enabledStrategy.description
            } as GTGTExemption))
        : [];
};

export const sortStrategyDetails = (strategyDetailsSectionInfoList: StrategyDetailsSectionInfo[]) => {
    const strategyTypePriority: Record<StrategyType, number> = {
        [StrategyType.STATE_OF_RESIDENCY]: 1,
        [StrategyType.ANNUAL_GIFTING]: 2,
        [StrategyType.GIFT_TO_GRANTOR_TRUST]: 3,
        [StrategyType.TWO_YEAR_GRAT]: 4,
        [StrategyType.FIVE_YEAR_GRAT]: 5,
        [StrategyType.ROLLING_GRAT]: 6
    };

    return strategyDetailsSectionInfoList.sort((a, b) => {
        if (a.enabled !== b.enabled) {
            return a.enabled ? -1 : 1;
        }
        return strategyTypePriority[a.type] - strategyTypePriority[b.type];
    });
};